import { useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import { type Dispatch, type UnknownAction } from "redux";

import {
  addAdministrativeAreaTypesDetails as addAdministrativeAreaTypesDetailsAction,
  updateAdministrativeAreaTypesDetails as updateAdministrativeAreaTypesDetailsAction,
} from "../../../../actions";
import { WFPButton } from "../../../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../../components/ui/dialog";
import { ALL_ITEMS_PAGE_SIZE } from "../../../../constants/pagination";
import { getCountryObjectFromCache } from "../../../../utils";
import { getTitleName } from "../../../../utils/wfpManagerConfigurationUtils";
import {
  type AdministrativeAreaType,
  type AdministrativeAreaTypesModalOptions,
  type EntityId,
  type SetAdministrativeAreaTypesModalOptions,
} from "../../types";
import { type AdministrativeAreaTypeFormValues, Form } from "../Form";

interface AdministrativeAreaTypesListState {
  data: {
    results?: AdministrativeAreaType[];
  };
  isFetching: boolean;
}

interface RootState {
  listAdministrativeAreaTypes: AdministrativeAreaTypesListState;
}

interface StateProps {
  administrativeAreaTypes: AdministrativeAreaType[];
  isFetching: boolean;
}

interface OwnProps {
  modalOptions?: AdministrativeAreaTypesModalOptions;
  setModalOptions: SetAdministrativeAreaTypesModalOptions;
}

interface DispatchProps {
  addAdministrativeAreaTypesDetails: (values: AdministrativeAreaType) => void;
  updateAdministrativeAreaTypesDetails: (id: EntityId, values: AdministrativeAreaType) => void;
}

type AdministrativeAreaTypesEditModalProps = StateProps & OwnProps & DispatchProps;
type AppDispatch = Dispatch<UnknownAction>;

const CLOSED_MODAL_OPTIONS: AdministrativeAreaTypesModalOptions = {
  id: undefined,
  isOpen: false,
  type: undefined,
};

const dispatchAction = (dispatch: AppDispatch, action: unknown) =>
  dispatch(action as UnknownAction);

const isEntityId = (id: AdministrativeAreaTypesModalOptions["id"]): id is EntityId =>
  id !== undefined && id !== "add";

const getCurrentCountryId = () => {
  const country = getCountryObjectFromCache() as { id?: EntityId | null } | null | undefined;

  return country?.id;
};

export const adaptFormValues = (
  formValues: AdministrativeAreaTypeFormValues
): AdministrativeAreaType => ({
  ...formValues,
  country: getCurrentCountryId(),
});

// The detail endpoint omits `parent`, so the form is prefilled from the list row instead.
export const formatInitialValues = (
  areaType: AdministrativeAreaType | undefined
): AdministrativeAreaTypeFormValues | undefined =>
  areaType && {
    active: Boolean(areaType.active),
    admin_level: areaType.admin_level ?? undefined,
    country: areaType.country ?? undefined,
    id: areaType.id ?? undefined,
    name: areaType.name ?? "",
    parent: areaType.parent ?? null,
  };

const getDialogTitle = (
  modalOptions: AdministrativeAreaTypesModalOptions,
  t: (key: string) => string
) => {
  const actionTitleKey = getTitleName(modalOptions.type);
  const baseTitle = t("configuration.item.administrativeAreaTypes");

  return actionTitleKey ? `${baseTitle} ${t(actionTitleKey)}` : baseTitle;
};

const getAdministrativeAreaTypesForCurrentCountry = (
  administrativeAreaTypes: AdministrativeAreaType[]
) => {
  const countryId = getCurrentCountryId();

  if (countryId === undefined || countryId === null) {
    return administrativeAreaTypes;
  }

  return administrativeAreaTypes.filter(areaType => String(areaType.country) === String(countryId));
};

export const AdministrativeAreaTypesEditModal = ({
  administrativeAreaTypes = [],
  isFetching,
  modalOptions = CLOSED_MODAL_OPTIONS,
  setModalOptions,
  updateAdministrativeAreaTypesDetails,
  addAdministrativeAreaTypesDetails,
}: AdministrativeAreaTypesEditModalProps) => {
  const { t } = useTranslation();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const shouldShowCancelButton = modalOptions.type !== "view";
  const validateText =
    modalOptions.type === "view" ? t("common.back") : t("negotiationCard.confirm");
  const administrativeAreaTypesFilteredByCountry = useMemo(
    () => getAdministrativeAreaTypesForCurrentCountry(administrativeAreaTypes),
    [administrativeAreaTypes]
  );
  const initialValues = useMemo(() => {
    if (modalOptions.type === "create" || !isEntityId(modalOptions.id)) {
      return undefined;
    }

    return formatInitialValues(
      administrativeAreaTypesFilteredByCountry.find(
        areaType => String(areaType.id) === String(modalOptions.id)
      )
    );
  }, [administrativeAreaTypesFilteredByCountry, modalOptions.id, modalOptions.type]);

  const handleClose = () => {
    setModalOptions(CLOSED_MODAL_OPTIONS);
  };

  const handleOpenChange = (isOpen: boolean) => {
    if (isOpen) {
      return;
    }

    handleClose();
  };

  const handleSubmitEdit = (formValues: AdministrativeAreaType) => {
    if (isEntityId(modalOptions.id)) {
      updateAdministrativeAreaTypesDetails(modalOptions.id, formValues);
    }
  };

  const handleSubmitCreate = (formValues: AdministrativeAreaType) => {
    addAdministrativeAreaTypesDetails(formValues);
  };

  const handleSubmit = (formValues: AdministrativeAreaTypeFormValues) => {
    const adaptedValues = adaptFormValues(formValues);

    if (modalOptions.type === "edit") {
      handleSubmitEdit(adaptedValues);
    }

    if (modalOptions.type === "create") {
      handleSubmitCreate(adaptedValues);
    }

    handleClose();
  };

  const handleValidate = () => {
    if (modalOptions.type === "view") {
      handleClose();
      return;
    }

    buttonRef.current?.click();
  };

  return (
    <Dialog open={Boolean(modalOptions.isOpen)} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>{getDialogTitle(modalOptions, t)}</DialogTitle>
        </DialogHeader>
        <Form
          initialValues={initialValues}
          isFetching={isFetching}
          modalOptions={modalOptions}
          onSubmit={handleSubmit}
          buttonRef={buttonRef}
          administrativeAreaTypes={administrativeAreaTypesFilteredByCountry}
        />
        <DialogFooter>
          {shouldShowCancelButton ? (
            <WFPButton type="button" variant="outline" onClick={handleClose}>
              {t("common.cancel")}
            </WFPButton>
          ) : null}
          <WFPButton type="button" onClick={handleValidate}>
            {validateText}
          </WFPButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const mapStateToProps = (state: RootState): StateProps => {
  return {
    administrativeAreaTypes: state.listAdministrativeAreaTypes.data.results ?? [],
    isFetching: state.listAdministrativeAreaTypes.isFetching,
  };
};

export const mapDispatchToProps = (dispatch: AppDispatch): DispatchProps => {
  return {
    addAdministrativeAreaTypesDetails: values =>
      dispatchAction(
        dispatch,
        addAdministrativeAreaTypesDetailsAction(values, "", true, {
          page_size: ALL_ITEMS_PAGE_SIZE,
        })
      ),
    updateAdministrativeAreaTypesDetails: (id, values) =>
      dispatchAction(
        dispatch,
        updateAdministrativeAreaTypesDetailsAction(id, values, "", true, {
          page_size: ALL_ITEMS_PAGE_SIZE,
        })
      ),
  };
};

export const ConnectedAdministrativeAreaTypesEditModal = connect(
  mapStateToProps,
  mapDispatchToProps
)(AdministrativeAreaTypesEditModal);
